/**
 * IndustryLibraryPage — Super-admin CRUD surface for the global
 * industry profile library that powers the 3-layer ROI resolver.
 *
 * Layers:
 *   Org override ▶ Industry profile ▶ Global defaults
 *
 * This page edits the middle layer. Org admins manage their
 * org-level overrides via the Mission Control "Industry model" panel.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import authedFetch from '../lib/authedFetch';
import { toast } from 'sonner';
import {
  Library, Plus, Pencil, Archive, ArchiveRestore, Trash2, Search,
  Layers, Loader2, Copy, Users, TrendingUp, Eye, EyeOff,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import PageShell from '../components/layout/PageShell';
import PageHero from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
import {
  API, ICON_MAP, slugify, formatCompactDollar,
  healthTier, trendDisplay, emptyDraft,
} from './industry/helpers';
import IndustryEditorModal from './industry/IndustryEditorModal';
import AssignedOrgsSheet from './industry/AssignedOrgsSheet';
import QuickFixModal from './industry/QuickFixModal';
import ActivityFeed from './industry/ActivityFeed';

export default function IndustryLibraryPage() {
  const [profiles, setProfiles] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null); // draft object or null
  const [editingTab, setEditingTab] = useState('basics');
  const [isCreate, setIsCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  // Drill-down state — null when the side-panel is closed, otherwise
  // { industry_id, industry_name } so the panel can fetch + show.
  const [drillDown, setDrillDown] = useState(null);
  // Quick-fix modal state — null or { profile, breakdown, score, stats }.
  const [quickFix, setQuickFix] = useState(null);
  // Set of industry ids the current user is watching. Loaded alongside
  // the rest of the library so cards can render the right toggle state.
  const [watches, setWatches] = useState(() => new Set());
  // Bumped after each profile mutation so the ActivityFeed re-fetches
  // without us lifting its state up here.
  const [activityNonce, setActivityNonce] = useState(0);
  const pageTheme = useMemo(() => getLightSectionTheme('Indigo'), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [profRes, metaRes, analyticsRes, watchRes] = await Promise.all([
        authedFetch(`${API}/api/industry-profiles?include_unpublished=true`),
        authedFetch(`${API}/api/industry-profiles/metadata`),
        authedFetch(`${API}/api/industry-profiles/analytics`),
        authedFetch(`${API}/api/industry-profiles/watches`),
      ]);
      if (!profRes.ok) throw new Error(await profRes.text());
      const profData = await profRes.json();
      setProfiles(profData?.profiles || []);
      if (metaRes.ok) {
        const m = await metaRes.json();
        setMetadata(m);
      }
      if (analyticsRes.ok) {
        const a = await analyticsRes.json();
        setAnalytics(a?.analytics || {});
      }
      if (watchRes.ok) {
        const w = await watchRes.json();
        setWatches(new Set(w?.watches || []));
      }
    } catch (e) {
      toast.error(`Failed to load library: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleWatch = async (p) => {
    const wasWatching = watches.has(p.id);
    // Optimistic flip — cards stay responsive even on slow connections.
    setWatches((prev) => {
      const next = new Set(prev);
      if (wasWatching) next.delete(p.id);
      else next.add(p.id);
      return next;
    });
    try {
      const res = await authedFetch(`${API}/api/industry-profiles/${p.id}/watch`, {
        method: wasWatching ? 'DELETE' : 'POST',
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success(wasWatching
        ? `Unwatched ${p.name}`
        : `Watching ${p.name} — you'll be notified when another admin edits it`);
    } catch (e) {
      // Revert on failure so the UI stays consistent with server state.
      setWatches((prev) => {
        const next = new Set(prev);
        if (wasWatching) next.add(p.id);
        else next.delete(p.id);
        return next;
      });
      toast.error(`Watch toggle failed: ${e.message || e}`);
    }
  };

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return profiles
      .filter((p) => (showArchived ? true : p.published !== false))
      .filter((p) => {
        if (!q) return true;
        return (
          p.id.includes(q) ||
          (p.name || '').toLowerCase().includes(q) ||
          (p.short_label || '').toLowerCase().includes(q) ||
          (p.description || '').toLowerCase().includes(q)
        );
      });
  }, [profiles, search, showArchived]);

  const stats = useMemo(() => {
    const total = profiles.length;
    const archived = profiles.filter((p) => p.published === false).length;
    const published = total - archived;
    // Aggregate orgs across all industries (excluding "_unassigned").
    let assignedOrgs = 0;
    let unassignedOrgs = 0;
    let totalHeadcount = 0;
    let needsAttention = 0;
    for (const [id, row] of Object.entries(analytics)) {
      if (id === '_unassigned') {
        unassignedOrgs += row.org_count || 0;
        continue;
      }
      assignedOrgs += row.org_count || 0;
      totalHeadcount += row.total_headcount || 0;
      if (typeof row.health_score === 'number' && row.health_score < 40) {
        needsAttention += 1;
      }
    }
    return [
      { label: 'Published', value: String(published) },
      { label: 'Needs attention', value: String(needsAttention) },
      { label: 'Orgs assigned', value: String(assignedOrgs) + (unassignedOrgs ? ` · ${unassignedOrgs} unset` : '') },
      { label: 'Total headcount', value: totalHeadcount >= 1000 ? `${(totalHeadcount / 1000).toFixed(1)}k` : String(totalHeadcount) },
    ];
  }, [profiles, analytics]);

  const openCreate = () => {
    setEditing(emptyDraft());
    setIsCreate(true);
  };

  const openEdit = (p, tab = 'basics') => {
    setEditing({
      ...emptyDraft(),
      ...p,
      // Hydrate numeric inputs as strings so empty != 0.
      portfolio_dampening_factor:
        p.portfolio_dampening_factor == null ? '' : String(p.portfolio_dampening_factor),
      max_kpi_uplift_percent:
        p.max_kpi_uplift_percent == null ? '' : String(p.max_kpi_uplift_percent),
      kpi_priorities: p.kpi_priorities || [],
      kpi_baselines: p.kpi_baselines || [],
      lei_kpi_weights: p.lei_kpi_weights || [],
      elasticities: p.elasticities || [],
    });
    setEditingTab(tab);
    setIsCreate(false);
  };

  const openClone = (p) => {
    // Seed the create modal with the source profile's coefficients but
    // a blank slug + " (copy)" name so super admins can spin up close
    // variants (e.g. Healthcare → Biotech) in a couple of edits.
    setEditing({
      ...emptyDraft(),
      ...p,
      id: '',
      name: `${p.name} (copy)`,
      short_label: p.short_label || '',
      published: true,
      portfolio_dampening_factor:
        p.portfolio_dampening_factor == null ? '' : String(p.portfolio_dampening_factor),
      max_kpi_uplift_percent:
        p.max_kpi_uplift_percent == null ? '' : String(p.max_kpi_uplift_percent),
      // Deep-copy the array fields so the source profile isn't mutated
      // by inline edits in the modal.
      kpi_priorities: [...(p.kpi_priorities || [])],
      kpi_baselines: (p.kpi_baselines || []).map((b) => ({ ...b })),
      lei_kpi_weights: (p.lei_kpi_weights || []).map((w) => ({ ...w })),
      elasticities: (p.elasticities || []).map((e) => ({ ...e })),
    });
    setIsCreate(true);
  };

  const closeEditor = () => {
    setEditing(null);
    setIsCreate(false);
  };

  const buildPayload = (draft) => {
    const out = {
      name: (draft.name || '').trim(),
      short_label: (draft.short_label || '').trim(),
      description: (draft.description || '').trim(),
      icon: draft.icon || null,
      kpi_priorities: (draft.kpi_priorities || []).filter(Boolean),
      kpi_baselines: (draft.kpi_baselines || [])
        .filter((b) => b.kpi_id && b.baseline_per_employee !== '' && b.baseline_per_employee != null)
        .map((b) => ({
          kpi_id: b.kpi_id,
          baseline_per_employee: Number(b.baseline_per_employee),
          source: b.source || null,
        })),
      lei_kpi_weights: (draft.lei_kpi_weights || [])
        .filter((w) => w.measure_id && w.kpi_id && w.weight !== '' && w.weight != null)
        .map((w) => ({
          measure_id: w.measure_id,
          kpi_id: w.kpi_id,
          weight: Number(w.weight),
        })),
      elasticities: (draft.elasticities || [])
        .filter((e) => e.measure_id && e.elasticity_moderate !== '' && e.elasticity_moderate != null)
        .map((e) => ({
          measure_id: e.measure_id,
          elasticity_moderate: Number(e.elasticity_moderate),
        })),
      portfolio_dampening_factor:
        draft.portfolio_dampening_factor === '' || draft.portfolio_dampening_factor == null
          ? null
          : Number(draft.portfolio_dampening_factor),
      max_kpi_uplift_percent:
        draft.max_kpi_uplift_percent === '' || draft.max_kpi_uplift_percent == null
          ? null
          : Number(draft.max_kpi_uplift_percent),
      llm_industry_context: (draft.llm_industry_context || '').trim() || null,
      published: draft.published !== false,
    };
    return out;
  };

  const onSave = async () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.short_label.trim()) {
      toast.error('Name and short label are required');
      return;
    }
    if (isCreate && !editing.id.trim()) {
      toast.error('Slug id is required');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload(editing);
      let res;
      if (isCreate) {
        res = await authedFetch(`${API}/api/industry-profiles`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: slugify(editing.id), ...payload }),
        });
      } else {
        res = await authedFetch(`${API}/api/industry-profiles/${editing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || res.statusText);
      }
      toast.success(isCreate ? 'Industry profile created' : 'Industry profile updated');
      closeEditor();
      await load();
      setActivityNonce((n) => n + 1);
    } catch (e) {
      toast.error(`Save failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  const onArchive = async (p) => {
    if (!window.confirm(`Archive "${p.name}"? Orgs already assigned keep their resolved coefficients, but it disappears from new pickers.`)) return;
    try {
      const res = await authedFetch(`${API}/api/industry-profiles/${p.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(await res.text());
      toast.success(`Archived "${p.name}"`);
      await load();
      setActivityNonce((n) => n + 1);
    } catch (e) {
      toast.error(`Archive failed: ${e.message || e}`);
    }
  };

  const onRestore = async (p) => {
    try {
      const res = await authedFetch(`${API}/api/industry-profiles/${p.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ published: true }),
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success(`Restored "${p.name}"`);
      await load();
      setActivityNonce((n) => n + 1);
    } catch (e) {
      toast.error(`Restore failed: ${e.message || e}`);
    }
  };

  const onHardDelete = async (p) => {
    if (!window.confirm(`HARD DELETE "${p.name}" (${p.id})? This cannot be undone. Will fail if any org still references it.`)) return;
    try {
      const res = await authedFetch(`${API}/api/industry-profiles/${p.id}?hard=true`, { method: 'DELETE' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || res.statusText);
      }
      toast.success(`Deleted "${p.name}"`);
      await load();
      setActivityNonce((n) => n + 1);
    } catch (e) {
      toast.error(`Delete failed: ${e.message || e}`);
    }
  };

  return (
    <PageShell
      theme={pageTheme}
      hero={(
        <PageHero
          theme={pageTheme}
          icon={Library}
          title="Industry Library"
          description="CRUD the global industry profiles that power the 3-layer ROI resolver. Org admins inherit these and can layer field-level overrides on top."
          stats={stats}
          testId="industry-library-hero"
        />
      )}
      data-testid="industry-library-page"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 py-8 space-y-4">
        <ActivityFeed
          refreshKey={activityNonce}
          onJumpToIndustry={(industryId) => {
            // Soft-jump: scroll the corresponding card into view if it's
            // rendered. Silent no-op if the user is currently filtering
            // it out — they'll see the activity row but won't get an
            // ugly half-scroll into an empty list.
            const el = document.querySelector(`[data-testid="industry-row-${industryId}"]`);
            if (el && typeof el.scrollIntoView === 'function') {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.classList.add('ring-2', 'ring-indigo-400');
              setTimeout(() => el.classList.remove('ring-2', 'ring-indigo-400'), 1500);
            }
          }}
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[260px] max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, slug, or description…"
                className="pl-9"
                data-testid="industry-library-search"
              />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600 whitespace-nowrap">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                data-testid="industry-library-show-archived"
              />
              Show archived
            </label>
          </div>
          <Button onClick={openCreate} data-testid="industry-library-new-btn">
            <Plus className="h-4 w-4 mr-2" /> New industry profile
          </Button>
        </div>

        {loading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500">
            <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading library…
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="industry-library-empty">
            <Library className="mx-auto mb-3 h-8 w-8 text-indigo-400" />
            <p className="text-sm font-medium text-slate-700">No industry profiles match your search</p>
            <p className="mt-1 text-sm text-slate-500">
              Try clearing filters or create a new profile.
            </p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="industry-library-list">
            {filtered.map((p) => {
              const Icon = ICON_MAP[p.icon] || Layers;
              const archived = p.published === false;
              const stats = analytics[p.id] || { org_count: 0, total_headcount: 0, aggregate_baseline_value: 0 };
              const isUnused = stats.org_count === 0 && !archived;
              const tier = !archived ? healthTier(stats.health_score) : null;
              const trend = !archived ? trendDisplay(stats.trend_7d) : null;
              return (
                <li
                  key={p.id}
                  data-testid={`industry-row-${p.id}`}
                  className={`rounded-xl border bg-white p-4 shadow-sm flex flex-col ${
                    archived ? 'border-slate-200 opacity-70' : 'border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`shrink-0 rounded-lg p-2 ${archived ? 'bg-slate-100 text-slate-400' : 'bg-indigo-50 text-indigo-600'}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-semibold text-slate-900 truncate">{p.name}</h3>
                          {archived && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">Archived</Badge>}
                          {tier && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (stats.health_score >= 70) return;
                                setQuickFix({
                                  profile: p,
                                  score: stats.health_score,
                                  breakdown: stats.health_breakdown || {},
                                  stats: {
                                    org_count: stats.org_count || 0,
                                    total_headcount: stats.total_headcount || 0,
                                    last_assigned_at: stats.last_assigned_at,
                                  },
                                });
                              }}
                              disabled={stats.health_score >= 70}
                              className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${tier.classes} ${stats.health_score >= 70 ? 'cursor-default' : 'hover:shadow-sm hover:brightness-95 cursor-pointer'}`}
                              data-testid={`industry-health-${p.id}`}
                              title={
                                stats.health_breakdown
                                  ? `${stats.health_score >= 70 ? '' : 'Click to see quick fixes · '}` +
                                    `Completeness ${Math.round((stats.health_breakdown.completeness || 0) * 100)}% · ` +
                                    `Recency ${Math.round((stats.health_breakdown.recency || 0) * 100)}% · ` +
                                    `Adoption ${Math.round((stats.health_breakdown.adoption || 0) * 100)}% · ` +
                                    `Coverage ${Math.round((stats.health_breakdown.coverage || 0) * 100)}%`
                                  : tier.label
                              }
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${tier.dot}`} />
                              {stats.health_score} · {tier.label}
                            </button>
                          )}
                          {trend && (() => {
                            const TrendIcon = trend.icon;
                            return (
                              <span
                                className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${trend.classes}`}
                                data-testid={`industry-trend-${p.id}`}
                                title={
                                  stats.trend_7d
                                    ? `7-day change · was ${stats.trend_7d.baseline_score} on ${stats.trend_7d.baseline_date}`
                                    : '7-day change'
                                }
                              >
                                <TrendIcon className="h-2.5 w-2.5" />
                                {trend.label}
                              </span>
                            );
                          })()}
                          {isUnused && !tier && (
                            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50" data-testid={`industry-unused-${p.id}`}>
                              0 orgs · archive candidate?
                            </Badge>
                          )}
                          <span className="text-[10px] text-slate-400 font-mono">v{p.version || 1}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 font-mono truncate">{p.id}</div>
                        <p className="text-xs text-slate-600 mt-1 line-clamp-2">{p.description || p.short_label}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(p.kpi_priorities || []).slice(0, 3).map((kid, idx) => (
                      <span
                        key={kid}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800"
                      >
                        <span className="text-indigo-400">#{idx + 1}</span>
                        {kid.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-slate-600">
                    <div className="rounded border border-slate-100 bg-slate-50 px-2 py-1">
                      <div className="text-[9px] uppercase text-slate-400 tracking-wider">KPIs</div>
                      <div className="font-mono">{(p.kpi_baselines || []).length}</div>
                    </div>
                    <div className="rounded border border-slate-100 bg-slate-50 px-2 py-1">
                      <div className="text-[9px] uppercase text-slate-400 tracking-wider">Weights</div>
                      <div className="font-mono">{(p.lei_kpi_weights || []).length}</div>
                    </div>
                    <div className="rounded border border-slate-100 bg-slate-50 px-2 py-1">
                      <div className="text-[9px] uppercase text-slate-400 tracking-wider">Elasticity</div>
                      <div className="font-mono">{(p.elasticities || []).length}</div>
                    </div>
                  </div>

                  {/* Industry adoption mini-panel — pulled from /analytics
                   *  Click anywhere on the strip to drill into the assigned
                   *  orgs (super admin) — perfect for spotting misclassified
                   *  tenants like "this fintech is currently tagged retail".
                   */}
                  <button
                    type="button"
                    onClick={() => setDrillDown({ industry_id: p.id, industry_name: p.name })}
                    className={`mt-3 w-full rounded-lg border px-2.5 py-2 grid grid-cols-3 gap-2 text-left transition-colors ${
                      isUnused
                        ? 'border-amber-200 bg-amber-50/40 hover:bg-amber-50'
                        : 'border-indigo-100 bg-indigo-50/40 hover:bg-indigo-50'
                    }`}
                    data-testid={`industry-analytics-${p.id}`}
                    aria-label={`View orgs assigned to ${p.name}`}
                  >
                    <div className="flex flex-col items-start">
                      <div className="flex items-center gap-1 text-[9px] uppercase text-slate-500 tracking-wider">
                        <Users className="h-2.5 w-2.5" /> Orgs
                      </div>
                      <div className={`text-xs font-bold font-mono ${isUnused ? 'text-amber-700' : 'text-indigo-800'}`}>
                        {stats.org_count}
                      </div>
                    </div>
                    <div className="flex flex-col items-start">
                      <div className="text-[9px] uppercase text-slate-500 tracking-wider">Headcount</div>
                      <div className="text-xs font-bold font-mono text-slate-800">
                        {stats.total_headcount >= 1000
                          ? `${(stats.total_headcount / 1000).toFixed(1)}k`
                          : stats.total_headcount}
                      </div>
                    </div>
                    <div className="flex flex-col items-start">
                      <div className="flex items-center gap-1 text-[9px] uppercase text-slate-500 tracking-wider">
                        <TrendingUp className="h-2.5 w-2.5" /> Baseline ROI
                      </div>
                      <div className="text-xs font-bold font-mono text-slate-800">
                        {formatCompactDollar(stats.aggregate_baseline_value)}
                      </div>
                    </div>
                  </button>

                  <div className="mt-3 flex items-center justify-end gap-1 pt-2 border-t border-slate-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleWatch(p)}
                      data-testid={`industry-watch-${p.id}`}
                      className={watches.has(p.id) ? 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100' : 'text-slate-500'}
                      title={watches.has(p.id)
                        ? 'You\'ll be notified when other admins edit this. Click to unwatch.'
                        : 'Get notified when other admins edit this profile.'}
                    >
                      {watches.has(p.id)
                        ? <><Eye className="h-3.5 w-3.5 mr-1" /> Watching</>
                        : <><EyeOff className="h-3.5 w-3.5 mr-1" /> Watch</>}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(p)}
                      data-testid={`industry-edit-${p.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openClone(p)}
                      data-testid={`industry-clone-${p.id}`}
                    >
                      <Copy className="h-3.5 w-3.5 mr-1" /> Clone
                    </Button>
                    {archived ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRestore(p)}
                        data-testid={`industry-restore-${p.id}`}
                      >
                        <ArchiveRestore className="h-3.5 w-3.5 mr-1" /> Restore
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onArchive(p)}
                        data-testid={`industry-archive-${p.id}`}
                      >
                        <Archive className="h-3.5 w-3.5 mr-1" /> Archive
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onHardDelete(p)}
                      data-testid={`industry-delete-${p.id}`}
                      className="text-rose-600 hover:bg-rose-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {editing && (
        <IndustryEditorModal
          draft={editing}
          setDraft={setEditing}
          isCreate={isCreate}
          saving={saving}
          onCancel={closeEditor}
          onSave={onSave}
          metadata={metadata}
          initialTab={editingTab}
          peerProfiles={profiles}
        />
      )}

      {quickFix && (
        <QuickFixModal
          fix={quickFix}
          onClose={() => setQuickFix(null)}
          onJumpToEditor={(tab) => {
            const p = profiles.find((pp) => pp.id === quickFix.profile.id);
            if (p) openEdit(p, tab);
            setQuickFix(null);
          }}
          onArchive={async () => {
            const p = profiles.find((pp) => pp.id === quickFix.profile.id);
            setQuickFix(null);
            if (p) await onArchive(p);
          }}
          onDrillDown={() => {
            setDrillDown({
              industry_id: quickFix.profile.id,
              industry_name: quickFix.profile.name,
            });
            setQuickFix(null);
          }}
        />
      )}

      <AssignedOrgsSheet
        open={drillDown !== null}
        industry={drillDown}
        onClose={() => setDrillDown(null)}
        profiles={profiles}
        onReassigned={async () => { await load(); setActivityNonce((n) => n + 1); }}
      />
    </PageShell>
  );
}
